import { uploadSignatureFilestack, type IntentEnum } from '@goldcast/api/backend';
import * as filestack from 'filestack-js';

import { getEnvConfig } from '@/constants';

export interface FilestackSecurity {
  policy: string;
  signature: string;
}

type FilestackClient = ReturnType<typeof filestack.init>;
type FilestackUploadOptions = Parameters<FilestackClient['upload']>[1];
type FilestackStoreOptions = Parameters<FilestackClient['upload']>[2];
type FilestackUploadToken = Parameters<FilestackClient['upload']>[3];
type FilestackMultiUploadOptions = Parameters<FilestackClient['multiupload']>[1];
type FilestackMultiStoreOptions = Parameters<FilestackClient['multiupload']>[2];

interface UploadFileWithFilestackSecurityRetryArgs {
  client?: FilestackClient;
  file: File;
  uploadOptions: FilestackUploadOptions;
  storeOptions: FilestackStoreOptions;
  filestackIntent: FilestackIntentArgs;
  isSignedFilestackUploadsEnabled: boolean;
  uploadToken?: FilestackUploadToken;
}

interface UploadFilesWithFilestackSecurityRetryArgs {
  client?: FilestackClient;
  files: File[];
  uploadOptions: FilestackMultiUploadOptions;
  storeOptions: FilestackMultiStoreOptions;
  filestackIntent: FilestackIntentArgs;
  isSignedFilestackUploadsEnabled: boolean;
}

export type FilestackIntentArgs =
  | { intent: Extract<IntentEnum, 'organization_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'event_asset' | 'slide_filler_asset'>; assetKind: string; event: string }
  | {
      intent: Extract<IntentEnum, 'content_lab_custom_asset'>;
      assetKind: string;
      projectId: string;
      contentId: string;
    }
  | { intent: Extract<IntentEnum, 'brand_kit_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'clip_overlay_asset' | 'content_lab_root_asset'>; assetKind: string };

function buildBody(args: FilestackIntentArgs): Record<string, string> {
  const body: Record<string, string> = { intent: args.intent, asset_kind: args.assetKind };
  if (args.intent === 'event_asset' || args.intent === 'slide_filler_asset') {
    body.event = args.event;
  } else if (args.intent === 'content_lab_custom_asset') {
    body.project_id = args.projectId;
    body.content_id = args.contentId;
  }
  return body;
}

function buildCacheKey(args: FilestackIntentArgs): string {
  const event = 'event' in args ? args.event : 'none';
  const project = 'projectId' in args ? args.projectId : 'none';
  const content = 'contentId' in args ? args.contentId : 'none';
  return `${args.intent}:${event}:${project}:${content}:${args.assetKind}`;
}

const EARLY_EXPIRY_BUFFER_MS = 60 * 1000;
const FILESTACK_REQUEST_ERROR_TYPE = 'request';
const FILESTACK_AUTH_STATUS_CODES = new Set([401, 403]);
const FILESTACK_SECURITY_ERROR_TOKENS = ['policy', 'signature', 'security', 'expired', 'unauthorized', 'forbidden'];

const securityCache = new Map<string, { security: FilestackSecurity; expiresAtMs: number }>();

export function invalidateFilestackSecurity(args: FilestackIntentArgs): void {
  securityCache.delete(buildCacheKey(args));
}

function getFilestackRequestDetails(error: Record<string, unknown>): Record<string, unknown> | null {
  if (error.type !== FILESTACK_REQUEST_ERROR_TYPE || !error.details || typeof error.details !== 'object') {
    return null;
  }
  return error.details as Record<string, unknown>;
}

function stringifyErrorDetail(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(value);
  }
  return '';
}

export function isFilestackSecurityError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const maybeError = error as Record<string, unknown>;
  const requestDetails = getFilestackRequestDetails(maybeError);
  if (!requestDetails) {
    return false;
  }

  if (typeof requestDetails.code === 'number' && FILESTACK_AUTH_STATUS_CODES.has(requestDetails.code)) {
    return true;
  }

  const searchableText = [maybeError.message, requestDetails.data].map(stringifyErrorDetail).join(' ').toLowerCase();

  return FILESTACK_SECURITY_ERROR_TOKENS.some(token => searchableText.includes(token));
}

export async function getFilestackSecurity(
  args: FilestackIntentArgs,
  options: { forceRefresh?: boolean } = {}
): Promise<{ security: FilestackSecurity }> {
  const cacheKey = buildCacheKey(args);
  const cached = securityCache.get(cacheKey);
  if (!options.forceRefresh && cached && Date.now() < cached.expiresAtMs - EARLY_EXPIRY_BUFFER_MS) {
    return { security: cached.security };
  }

  const response = (await uploadSignatureFilestack({ body: buildBody(args) as never })) as {
    policy: string;
    signature: string;
    expiresAt: number;
  };
  const security = { policy: response.policy, signature: response.signature };
  securityCache.set(cacheKey, { security, expiresAtMs: response.expiresAt * 1000 });
  return { security };
}

export async function uploadWithFilestackSecurityRetry<T>(
  args: FilestackIntentArgs,
  isSignedFilestackUploadsEnabled: boolean,
  upload: (security?: FilestackSecurity) => Promise<T>
): Promise<T> {
  if (!isSignedFilestackUploadsEnabled) {
    return upload(undefined);
  }

  const { security } = await getFilestackSecurity(args);
  try {
    return await upload(security);
  } catch (error) {
    if (!isFilestackSecurityError(error)) {
      throw error;
    }
    invalidateFilestackSecurity(args);
    const { security: refreshedSecurity } = await getFilestackSecurity(args, { forceRefresh: true });
    return upload(refreshedSecurity);
  }
}

export async function uploadFileWithFilestackSecurityRetry({
  client,
  file,
  uploadOptions,
  storeOptions,
  filestackIntent,
  isSignedFilestackUploadsEnabled,
  uploadToken
}: UploadFileWithFilestackSecurityRetryArgs): ReturnType<FilestackClient['upload']> {
  return uploadWithFilestackSecurityRetry(filestackIntent, isSignedFilestackUploadsEnabled, security => {
    const filestackClient = client ?? filestack.init(getEnvConfig('FILESTACK_API_KEY'));
    if (security) {
      return filestackClient.upload(file, uploadOptions, storeOptions, uploadToken, security);
    }
    return uploadToken
      ? filestackClient.upload(file, uploadOptions, storeOptions, uploadToken)
      : filestackClient.upload(file, uploadOptions, storeOptions);
  });
}

export async function uploadFilesWithFilestackSecurityRetry({
  client,
  files,
  uploadOptions,
  storeOptions,
  filestackIntent,
  isSignedFilestackUploadsEnabled
}: UploadFilesWithFilestackSecurityRetryArgs): ReturnType<FilestackClient['multiupload']> {
  return uploadWithFilestackSecurityRetry(filestackIntent, isSignedFilestackUploadsEnabled, security => {
    const filestackClient = client ?? filestack.init(getEnvConfig('FILESTACK_API_KEY'));
    return security
      ? filestackClient.multiupload(files, uploadOptions, storeOptions, undefined, security)
      : filestackClient.multiupload(files, uploadOptions, storeOptions);
  });
}
